import React, { useState, useEffect, useContext } from 'react';
import { FaTrash, FaPlus, FaCalculator } from '../../components/Icons';
import { Table, TableContainer, Th, Td, SectionTitle, ActionButton, Label, FormGroup, Input, Select, TextArea, Card, Button } from '../../components/StyledTableComponents';
import { ConfiguracionLastres } from '../../models/types';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { AppContext } from '../../context/AppContext';
import { Liga, Carrera, SistemaPuntuacion } from '../../models/types';
import { sortRacesByName } from '../../utils/sortRacesByName';
import { LigaServiceFirestore } from '../../services/LigaServiceFirestore';
import CarreraService from '../../services/CarreraService';
import { SocioServiceFirestore, Socio } from '../../services/SocioServiceFirestore';
import { FaSave, FaArrowLeft, FaEdit, FaTrophy, FaUsers, FaFlag } from '../../components/Icons';
import LastresInstrucciones from './LastresInstrucciones';
import { theme } from '../../theme';

const Container = styled.div`
  padding: 24px;
  max-width: 1280px;
  margin: 0 auto;
  background-color: ${theme.colors.background.default};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  color: ${theme.colors.primary.main};
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: 2.2rem;
  position: relative;
  padding-bottom: 12px;
  display: flex;
  align-items: center;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 4px;
    background-color: ${theme.colors.secondary.main};
    border-radius: 2px;
  }
  
  svg {
    margin-right: 16px;
    color: ${theme.colors.secondary.main};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;



const BackButton = styled.button`
  background-color: ${theme.colors.background.dark};
  color: ${theme.colors.secondary.contrastText};
  border: none;
  border-radius: ${theme.borderRadius.small};
  padding: 12px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-weight: ${theme.typography.fontWeights.medium};
  transition: all 0.3s ease;
  box-shadow: ${theme.shadows.small};
  
  &:hover {
    background-color: ${theme.colors.primary.light};
    color: ${theme.colors.primary.contrastText};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
  }
  
  svg {
    margin-right: 10px;
    font-size: 1.1rem;
  }
`;





const Form = styled.form`
  background-color: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.medium};
  padding: 32px;
  box-shadow: ${theme.shadows.medium};
  margin-bottom: 32px;
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const LigaDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, loadData } = useContext(AppContext);
  const [liga, setLiga] = useState<Liga | null>(null);
  const [formData, setFormData] = useState<{
    nombre: string;
    descripcion: string;
    sistemaPuntuacion: string;
    categoria: string;
    configuracionLastres: ConfiguracionLastres;
  }>({
    nombre: '',
    descripcion: '',
    sistemaPuntuacion: '',
    categoria: '',
    configuracionLastres: {
      lastrePorPosicion: [
        { posicion: 1, kg: 40 },
        { posicion: 2, kg: 36 },
        { posicion: 3, kg: 32 },
        { posicion: 4, kg: 28 },
        { posicion: 5, kg: 24 },
        { posicion: 6, kg: 20 },
        { posicion: 7, kg: 16 },
        { posicion: 8, kg: 12 },
        { posicion: 9, kg: 8 },
        { posicion: 10, kg: 4 },
      ],
      aplicacion: [
        { carreras: [1], tipo: 'sin_lastres' },
        { carreras: [2, 3], tipo: 'por_puntos_acumulados' },
        { carreras: [4, 5, 6], tipo: 'por_puntos_descartando_peor', descartarSiMenorQue: -2 }
      ],
      reduccionPorEquipo: [
        { puesto: 1, kg: -7 },
        { puesto: 2, kg: -4 },
        { puesto: 3, kg: -2 },
        { puesto: 4, kg: -1 },
        { puesto: 5, kg: 0 }
      ],
      // Configuración por defecto para equipos (3 pilotos por equipo)
      configuracionEquipos: {
        pilotosPorEquipo: 3
      }
    }
  });
  const [carreras, setCarreras] = useState<Carrera[]>([]);
  const [sociosLiga, setSociosLiga] = useState<Socio[]>([]);

  useEffect(() => {
    if (id === 'new') {
      setLiga(null);
      setFormData({
        nombre: '',
        descripcion: '',
        sistemaPuntuacion: data.sistemasPuntuacion.length > 0 ? data.sistemasPuntuacion[0].id : '',
        categoria: '',
        configuracionLastres: {
          lastrePorPosicion: [],
          aplicacion: [],
          reduccionPorEquipo: [],
          configuracionEquipos: {
            pilotosPorEquipo: 3
          }
        }
      });
      setSociosLiga([]);
    } else {
      const ligaExistente = data.ligas.find((l: { id: string }) => l.id === id);
      if (ligaExistente) {
        setLiga(ligaExistente);
        let categoria = '';
        if (ligaExistente.nombre === 'PRO' || ligaExistente.nombre.includes('PRO')) {
          categoria = 'PRO';
        } else if (ligaExistente.nombre === 'ADV' || ligaExistente.nombre.includes('ADV')) {
          categoria = 'ADV';
        } else if (ligaExistente.nombre === 'NOV' || ligaExistente.nombre.includes('NOV')) {
          categoria = 'NOV';
        }

        // Asegurarse de que la configuración de equipos tenga un valor por defecto si no existe
        const configuracionEquipos = ligaExistente.configuracionLastres?.configuracionEquipos || {
          pilotosPorEquipo: 3
        };
        
        setFormData({
          nombre: ligaExistente.nombre,
          descripcion: ligaExistente.descripcion,
          sistemaPuntuacion: ligaExistente.sistemaPuntuacion,
          categoria: categoria,
          configuracionLastres: {
            ...(ligaExistente.configuracionLastres || {
              lastrePorPosicion: [
                { posicion: 1, kg: 40 },
                { posicion: 2, kg: 36 },
                { posicion: 3, kg: 32 },
                { posicion: 4, kg: 28 },
                { posicion: 5, kg: 24 },
                { posicion: 6, kg: 20 },
                { posicion: 7, kg: 16 },
                { posicion: 8, kg: 12 },
                { posicion: 9, kg: 8 },
                { posicion: 10, kg: 4 },
              ],
              aplicacion: [
                { carreras: [1], tipo: 'sin_lastres' },
                { carreras: [2, 3], tipo: 'por_puntos_acumulados' },
                { carreras: [4, 5, 6], tipo: 'por_puntos_descartando_peor', descartarSiMenorQue: -2 }
              ],
              reduccionPorEquipo: [
                { puesto: 1, kg: -7 },
                { puesto: 2, kg: -4 },
                { puesto: 3, kg: -2 },
                { puesto: 4, kg: -1 },
                { puesto: 5, kg: 0 }
              ]
            }),
            // Asegurarse de que la configuración de equipos esté presente
            configuracionEquipos: configuracionEquipos
          }
        });
        const carrerasLiga = data.carreras.filter((c: { id: string; ligaId: string }) => c.ligaId === ligaExistente.id);
        setCarreras(sortRacesByName(carrerasLiga));
        SocioServiceFirestore.getAll().then((socios) => {
          // Usar directamente la categoría de la liga si existe
          const categoriaFiltro = ligaExistente.categoria || '';
          
          // Si hay categoría definida, filtrar por ella
          if (categoriaFiltro) {
            const sociosDeLiga = socios.filter((s: { Categoria?: string }) => {
              const socioCategoria = (typeof s.Categoria === 'string' ? s.Categoria : '').trim().toUpperCase();
              return socioCategoria === categoriaFiltro.toUpperCase();
            });
            setSociosLiga(sociosDeLiga);
          } else {
            // Si no hay categoría definida, mostrar todos los pilotos
            setSociosLiga(socios);
          }
        });
      }
    }
  }, [id, data]);
  
  // Maneja cambios en los formularios
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Si cambia la liga, cargar los pilotos de esa liga
    if (name === 'ligaId' && value) {
      const ligaSeleccionada = data.ligas.find((l: { id: string }) => l.id === value);
      if (ligaSeleccionada) {
        // Cargar las carreras de la liga seleccionada
        const carrerasLiga = data.carreras.filter((c: { id: string; ligaId: string }) => c.ligaId === ligaSeleccionada.id);
        setCarreras(sortRacesByName(carrerasLiga));
        
        // Cargar los pilotos de la liga seleccionada
        SocioServiceFirestore.getAll().then((socios) => {
          // Usar directamente la categoría de la liga si existe
          const categoriaFiltro = ligaSeleccionada.categoria || '';
          
          // Si hay categoría definida, filtrar por ella
          if (categoriaFiltro) {
            const sociosDeLiga = socios.filter((s: { Categoria?: string }) => {
              const socioCategoria = (typeof s.Categoria === 'string' ? s.Categoria : '').trim().toUpperCase();
              return socioCategoria === categoriaFiltro.toUpperCase();
            });
            setSociosLiga(sociosDeLiga);
          } else {
            // Si no hay categoría definida, mostrar todos los pilotos
            setSociosLiga(socios);
          }
        });
      }
    }
  };

  // Maneja el submit del formulario de liga
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (id === 'new') {
      await LigaServiceFirestore.create({
        nombre: formData.nombre,
        descripcion: formData.descripcion,
        carreras: [],
        sistemaPuntuacion: formData.sistemaPuntuacion,
        categoria: formData.categoria,
        configuracionLastres: formData.configuracionLastres
      });
    } else if (liga) {
      await LigaServiceFirestore.update(liga.id, {
        nombre: formData.nombre,
        descripcion: formData.descripcion,
        sistemaPuntuacion: formData.sistemaPuntuacion,
        categoria: formData.categoria,
        configuracionLastres: formData.configuracionLastres
      });
    }
    await loadData();
    navigate('/ligas');
  };

  // Elimina una carrera de la liga
  const handleRemoveCarrera = async (carreraId: string) => {
    if (!liga) return;
    const nuevasCarreras = carreras.filter((c: { id: string }) => c.id !== carreraId);
    setCarreras(nuevasCarreras);
    await LigaServiceFirestore.update(liga.id, {
      carreras: nuevasCarreras.map((c: { id: string }) => c.id)
    });
    await loadData();
  };

  const sincronizarCarreras = async () => {
    if (!liga) return;
    
    try {
      const carrerasLiga = data.carreras.filter((c: { id: string; ligaId: string }) => c.ligaId === liga.id);
      const carrerasIds = carrerasLiga.map((c: { id: string }) => c.id);
      await LigaServiceFirestore.update(liga.id, {
        carreras: carrerasIds
      });
      await loadData();
      setCarreras(sortRacesByName(carrerasLiga));
      alert(`Sincronización completada. Se encontraron ${carrerasIds.length} carreras para esta liga.`);
    } catch (error) {
      console.error('ERROR - sincronizarCarreras:', error);
      alert('Error al sincronizar las carreras. Por favor, inténtalo de nuevo.');
    }
  };
  return (
    <Container>
      <Header>
        <Title><FaTrophy /> {id === 'new' ? 'Nueva Liga' : 'Editar Liga'}</Title>
        <ButtonGroup>
          <BackButton onClick={() => navigate('/ligas')}>
            <FaArrowLeft /> Volver
          </BackButton>
          <Button onClick={handleSubmit} type="button">
            <FaSave /> Guardar
          </Button>
          {id !== 'new' && (
            <Button onClick={() => navigate(`/ligas/${id}/lastres`)} type="button" style={{ marginLeft: '10px', backgroundColor: '#e94560' }}>
              <FaCalculator /> Ver Lastres
            </Button>
          )}
        </ButtonGroup>
      </Header>
      
      <Form onSubmit={handleSubmit}>
        <SectionTitle><FaCalculator /> Información de la Liga</SectionTitle>
        <FormGroup>
          <Label htmlFor="nombre">Nombre de la Liga</Label>
          <Input
            id="nombre"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            placeholder="seguremos este formato: 2025-Invierno-PRO"
            required
          />
          <p style={{fontSize: '0.85em', color: '#666', marginTop: '5px'}}>
          seguremos este formato: "2025-Invierno-PRO"
          </p>
        </FormGroup>
        
        <FormGroup>
          <Label>Categoría:</Label>
          <Select
            value={formData.categoria}
            onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
          >
            <option value="">Seleccione una categoría</option>
            <option value="PRO">PRO</option>
            <option value="ADV">ADV</option>
            <option value="NOV">NOV</option>
          </Select>
          <small style={{fontSize: '0.85em', color: '#666', marginTop: '5px'}}>
            La categoría determina qué pilotos participan en esta liga (basado en su campo Categoria).
          </small>
        </FormGroup>
        
        <FormGroup>
          <Label>Pilotos por equipo (puntuación):</Label>
          <Input
            type="number"
            min="1"
            max="10"
            value={formData.configuracionLastres.configuracionEquipos?.pilotosPorEquipo || 3}
            onChange={(e) => setFormData({
              ...formData,
              configuracionLastres: {
                ...formData.configuracionLastres,
                configuracionEquipos: {
                  ...(formData.configuracionLastres.configuracionEquipos || { pilotosPorEquipo: 3 }),
                  pilotosPorEquipo: parseInt(e.target.value) || 3
                }
              }
            })}
          />
          <small>Número de pilotos que cuentan para la puntuación del equipo en cada carrera</small>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="descripcion">Descripción</Label>
          <TextArea
            id="descripcion"
            name="descripcion"
            value={formData.descripcion}
            onChange={handleChange}
            required
          />
        </FormGroup>

        {/* --- Configuración de Lastres --- */}
        <SectionTitle>Configuración de Lastres</SectionTitle>
        <LastresInstrucciones />
        <FormGroup>
          <Label>Tabla de Lastre por Posición</Label>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th>Posición</Th>
                  <Th>Kg de Lastre</Th>
                  <Th>Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {formData.configuracionLastres.lastrePorPosicion.map((item: { posicion: number; kg: number }, idx: number) => (
                  <tr key={idx}>
                    <Td>
                      <Input
                        type="number"
                        min={1}
                        value={item.posicion}
                        onChange={e => {
                          const newVal = parseInt(e.target.value, 10);
                          setFormData(prev => ({
                            ...prev,
                            configuracionLastres: {
                              ...prev.configuracionLastres,
                              lastrePorPosicion: prev.configuracionLastres.lastrePorPosicion.map((it, i) => i === idx ? { ...it, posicion: newVal } : it)
                            }
                          }));
                        }}
                        style={{ width: 60 }}
                      />
                    </Td>
                    <Td>
                      <Input
                        type="number"
                        value={item.kg}
                        onChange={e => {
                          const newVal = parseInt(e.target.value, 10);
                          setFormData(prev => ({
                            ...prev,
                            configuracionLastres: {
                              ...prev.configuracionLastres,
                              lastrePorPosicion: prev.configuracionLastres.lastrePorPosicion.map((it, i) => i === idx ? { ...it, kg: newVal } : it)
                            }
                          }));
                        }}
                        style={{ width: 80 }}
                      />
                    </Td>
                    <Td>
                      <ActionButton
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            configuracionLastres: {
                              ...prev.configuracionLastres,
                              lastrePorPosicion: prev.configuracionLastres.lastrePorPosicion.filter((_, i) => i !== idx)
                            }
                          }));
                        }}
                        title="Eliminar posición"
                      >
                        <FaTrash />
                      </ActionButton>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
          <Button type="button" onClick={() => {
            setFormData(prev => ({
              ...prev,
              configuracionLastres: {
                ...prev.configuracionLastres,
                lastrePorPosicion: [...prev.configuracionLastres.lastrePorPosicion, { posicion: prev.configuracionLastres.lastrePorPosicion.length + 1, kg: 0 }]
              }
            }));
          }}>
            <FaPlus /> Añadir posición
          </Button>
        </FormGroup>
        <FormGroup>
          <Label>Reglas de Aplicación de Lastre</Label>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th>Carreras</Th>
                  <Th>Tipo de Regla</Th>
                  <Th>Descartar si &lt;</Th>
                  <Th>Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {formData.configuracionLastres.aplicacion.map((regla: { carreras: number[]; tipo: string; descartarSiMenorQue?: number }, idx: number) => (
                  <tr key={idx}>
                    <Td>
                      <Input
                        type="text"
                        value={regla.carreras.join(',')}
                        onChange={e => {
                          const carreras = e.target.value.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n));
                          setFormData(prev => ({
                            ...prev,
                            configuracionLastres: {
                              ...prev.configuracionLastres,
                              aplicacion: prev.configuracionLastres.aplicacion.map((r, i) => i === idx ? { ...r, carreras } : r)
                            }
                          }));
                        }}
                        style={{ width: 120 }}
                      />
                    </Td>
                    <Td>
                      <Select
                        value={regla.tipo}
                        onChange={e => {
                          setFormData(prev => ({
                            ...prev,
                            configuracionLastres: {
                              ...prev.configuracionLastres,
                              aplicacion: prev.configuracionLastres.aplicacion.map((r: { carreras: number[]; tipo: string; descartarSiMenorQue?: number }, i: number) => i === idx ? { ...r, tipo: e.target.value } : r)
                            }
                          }));
                        }}
                      >
                        <option value="sin_lastres">Sin lastres</option>
                        <option value="por_puntos_acumulados">Por puntos acumulados</option>
                        <option value="por_puntos_descartando_peor">Por puntos descartando peor</option>
                      </Select>
                    </Td>
                    <Td>
                      <Input
                        type="number"
                        value={regla.descartarSiMenorQue ?? ''}
                        disabled={regla.tipo !== 'por_puntos_descartando_peor'}
                        onChange={e => {
                          const descartarSiMenorQue = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                          setFormData(prev => ({
                            ...prev,
                            configuracionLastres: {
                              ...prev.configuracionLastres,
                              aplicacion: prev.configuracionLastres.aplicacion.map((r, i) => i === idx ? { ...r, descartarSiMenorQue } : r)
                            }
                          }));
                        }}
                        style={{ width: 90 }}
                      />
                    </Td>
                    <Td>
                      <ActionButton
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            configuracionLastres: {
                              ...prev.configuracionLastres,
                              aplicacion: prev.configuracionLastres.aplicacion.filter((_, i) => i !== idx)
                            }
                          }));
                        }}
                        title="Eliminar regla"
                      >
                        <FaTrash />
                      </ActionButton>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
          <Button type="button" onClick={() => {
            setFormData(prev => ({
              ...prev,
              configuracionLastres: {
                ...prev.configuracionLastres,
                aplicacion: [...prev.configuracionLastres.aplicacion, { carreras: [], tipo: 'sin_lastres' }]
              }
            }));
          }}>
            <FaPlus /> Añadir regla
          </Button>
        </FormGroup>
        <FormGroup>
          <Label>Reducción de Lastre por Resultado de Equipo</Label>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <Th>Puesto Equipo</Th>
                  <Th>Kg a Restar</Th>
                  <Th>Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {formData.configuracionLastres.reduccionPorEquipo.map((item: { puesto: number; kg: number }, idx: number) => (
                  <tr key={idx}>
                    <Td>
                      <Input
                        type="number"
                        min={1}
                        value={item.puesto}
                        onChange={e => {
                          const newVal = parseInt(e.target.value, 10);
                          setFormData(prev => ({
                            ...prev,
                            configuracionLastres: {
                              ...prev.configuracionLastres,
                              reduccionPorEquipo: prev.configuracionLastres.reduccionPorEquipo.map((it, i) => i === idx ? { ...it, puesto: newVal } : it)
                            }
                          }));
                        }}
                        style={{ width: 60 }}
                      />
                    </Td>
                    <Td>
                      <Input
                        type="number"
                        value={item.kg}
                        onChange={e => {
                          const newVal = parseInt(e.target.value, 10);
                          setFormData(prev => ({
                            ...prev,
                            configuracionLastres: {
                              ...prev.configuracionLastres,
                              reduccionPorEquipo: prev.configuracionLastres.reduccionPorEquipo.map((it, i) => i === idx ? { ...it, kg: newVal } : it)
                            }
                          }));
                        }}
                        style={{ width: 80 }}
                      />
                    </Td>
                    <Td>
                      <ActionButton
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            configuracionLastres: {
                              ...prev.configuracionLastres,
                              reduccionPorEquipo: prev.configuracionLastres.reduccionPorEquipo.filter((_, i) => i !== idx)
                            }
                          }));
                        }}
                        title="Eliminar reducción"
                      >
                        <FaTrash />
                      </ActionButton>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
          <Button type="button" onClick={() => {
            setFormData(prev => ({
              ...prev,
              configuracionLastres: {
                ...prev.configuracionLastres,
                reduccionPorEquipo: [...prev.configuracionLastres.reduccionPorEquipo, { puesto: prev.configuracionLastres.reduccionPorEquipo.length + 1, kg: 0 }]
              }
            }));
          }}>
            <FaPlus /> Añadir reducción
          </Button>
        </FormGroup>
        
        <SectionTitle><FaCalculator /> Sistema de Puntuación</SectionTitle>
        <FormGroup>
          <Label htmlFor="sistemaPuntuacion">Sistema de Puntuación</Label>
          <Select
            id="sistemaPuntuacion"
            name="sistemaPuntuacion"
            value={formData.sistemaPuntuacion}
            onChange={handleChange}
            required
          >
            {data.sistemasPuntuacion.map((sistema: SistemaPuntuacion) => (
              <option key={sistema.id} value={sistema.id}>
                {sistema.nombre}
              </option>
            ))}
          </Select>
        </FormGroup>
      </Form>
      
      {liga && (
        <>
          <Card>
            <SectionTitle><FaFlag /> Carreras en esta Liga</SectionTitle>
            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <Th>Nombre</Th>
                    <Th>Circuito</Th>
                    <Th>Fecha</Th>
                    <Th>Pilotos</Th>
                    <Th>Acciones</Th>
                  </tr>
                </thead>
                <tbody>
                  {carreras.map(carrera => (
                    <tr key={carrera.id}>
                      <Td>{carrera.nombre}</Td>
                      <Td>{carrera.circuito}</Td>
                      <Td>{carrera.fecha}</Td>
                      <Td>{carrera.resultados.length}</Td>
                      <Td>
                        <ActionButton onClick={() => navigate(`/carreras/${carrera.id}`)}>
                          <FaEdit />
                        </ActionButton>
                        <ActionButton onClick={() => handleRemoveCarrera(carrera.id)}>
                          <FaTrash />
                        </ActionButton>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          </Card>

          <Card>
            <SectionTitle><FaUsers /> Pilotos en esta Liga</SectionTitle>
            {sociosLiga.length > 0 ? (
              <TableContainer>
                <Table>
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Categoría</th>
                      <th>Equipo</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sociosLiga.map(socio => (
                      <tr key={socio.id}>
                        <td>{
  socio.display_name ||
  socio.NOMBRE_SERVERS_VRS ||
  socio.NOMBRE_CORTO ||
  socio.user_login ||
  socio.user_nicename ||
  `${socio.nombre || ''} ${socio.apellido || ''}`.trim() ||
  '-'
}</td>
                        <td>{socio.Categoria || '-'}</td>
                        <td>{socio.EQUIPO || '-'}</td>
                        <td>
                          <ActionButton onClick={() => navigate(`/socios/${socio.id}`)} title="Editar piloto">
                            <FaEdit />
                          </ActionButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>
            ) : (
              <p>No hay pilotos asignados a esta liga.</p>
            )}
          </Card>
        </>
      )}
    </Container>
  );
};

export default LigaDetail;
